import { Injectable } from '@angular/core';
import { FilesService } from './files.service';
import { DocumentDataService } from './document-data.service';
import { switchMap, take } from 'rxjs/operators';
import { EventService } from '@shared/event.service';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  pdfExtension = ['.pdf'];
  imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  textExtensions = ['.txt', '.md', '.json', '.xml', '.log'];
  previewableExtensions = [...this.pdfExtension, ...this.imageExtensions, ...this.textExtensions];

  private downloadReady = true;
  private downloadDebounce = 500;

  constructor(
    private _filesService: FilesService,
    private _eventService: EventService,
    private _translateService: TranslateService,
    private _documentDataService: DocumentDataService
  ) {}

  public download(targetOrganizationId: string) {
    if (this.downloadReady) {
      this.downloadReady = false;
      const nodes = this._filesService.getSelectedNodes();
      const items = this.filterFilesToDownload(nodes);

      if (!items.length) {
        this.clearDownloadReady();
        return;
      }

      const fallbackFilename = items.length === 1
        ? (items[0].split('/').pop() ?? items[0])
        : 'Files_' + DateTime.local().toSQLDate() + '.zip';

      this._documentDataService
        .getDownloadToken(items, targetOrganizationId)
        .pipe(
          take(1),
          switchMap(({ token }) =>
            this._documentDataService.downloadWithToken(targetOrganizationId, token, fallbackFilename)
          )
        )
        .subscribe({
          error: (error) => {
            const message = error?.error?.error || error?.message
              || this._translateService.instant('data-room.dialogs.download-failed');
            this._eventService.error(message);
          },
        });
    }
    this.clearDownloadReady();
  }

  public preview(targetOrganizationId: string, nodeOverride?: any) {
    if (this.downloadReady) {
      this.downloadReady = false;
      const node = nodeOverride || this._filesService.getSelectedNodes()[0];
      const item = [node.data.documentKey.key];
      const fileType = this.getFileType(node.data.path).toLowerCase();

      if (!this.previewableExtensions.includes(fileType)) {
        this.clearDownloadReady();
        return this._eventService.error(
          fileType +
            ' ' +
            this._translateService.instant('data-room.dialogs.preview-error')
        );
      }
      this._documentDataService
        .downloadDocumentsAsBlob(item, targetOrganizationId)
        .pipe(take(1))
        .subscribe({
          next: (data: Blob) => {
            if (this.pdfExtension.includes(fileType)) {
              this.displayPdf(data);
            } else if (this.imageExtensions.includes(fileType)) {
              this.displayImage(data);
            }
          },
          error: () => {
            this._eventService.error(
              this._translateService.instant('data-room.dialogs.download-failed')
            );
          },
        });
    }
    this.clearDownloadReady();
  }

  private filterFilesToDownload(nodes: any[]): string[] {
    const uniquePaths = new Set<string>();

    const processNodes = (nodeList: any[]) => {
      for (const node of nodeList) {
        const isValidData = node && node.data && node.data.documentKey;

        if (isValidData) {
          if (node.data.documentKey.docType === 'FILE') {
            uniquePaths.add(node.data.documentKey.key);
          }

          if (isValidData && node.data.documentKey.docType === 'PATH' && node.childrenAfterAggFilter?.length > 0) {
            processNodes(node.childrenAfterAggFilter);
          }
        }
      }
    };

    processNodes(nodes);
    return Array.from(uniquePaths);
  }

  private clearDownloadReady() {
    setTimeout(() => {
      this.downloadReady = true;
    }, this.downloadDebounce);
  }

  private displayPdf(data) {
    const file = new Blob([data], { type: 'application/pdf' });
    const fileUrl = URL.createObjectURL(file);
    window.open(fileUrl, '_blank');
  }

  private displayImage(data) {
    const reader = new FileReader();
    reader.readAsDataURL(data);
    reader.onloadend = () => {
      const image = new Image();
      image.src = reader.result.toString();
      image.style.maxWidth = '800px';
      image.style.display = 'block';
      image.style.margin = 'auto';
      const w = window.open('');
      w.document.write(image.outerHTML);
    };
  }

  getFileType(fileName: string) {
    return (
      '.' + fileName.split('.')[fileName.split('.').length - 1].toLowerCase()
    );
  }
}
